import React, { useEffect, useRef, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import styles from './SequenceTimeline.module.css';

const SequenceTimeline = ({
  items = [],
  selectedItemId = null,
  selectedPerspective = 'front',
  dragEnabled = false,
  onReorder,
  onSelectItem,
}) => {
  const selectedItemRef = useRef(null);
  const [videoSources, setVideoSources] = useState({});
  const [hoveredPreview, setHoveredPreview] = useState(null);

  useEffect(() => {
    if (!selectedItemRef.current) return;

    selectedItemRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [selectedItemId]);

  useEffect(() => {
    if (!items || items.length === 0 || !selectedPerspective) return;

    items.forEach((item) => {
      if (!item?.content_id) return;

      const key = `${item.content_id}-${selectedPerspective}`;

      if (videoSources[key] !== undefined) return;

      fetch(`/api/content/${item.content_id}/perspectives?perspective=${selectedPerspective}`)
        .then((res) => res.json())
        .then((data) => {
          let selectedVideo = null;

          if (data.videos && data.videos.length > 0) {
            const mainVideo = data.videos.find((video) => video.is_main);
            selectedVideo = mainVideo
              ? mainVideo.video_path
              : data.videos[0].video_path;
          }

          setVideoSources((prev) => ({
            ...prev,
            [key]: selectedVideo,
          }));
        })
        .catch((err) => {
          console.error(
            `Error fetching timeline preview for content_id=${item.content_id}, perspective=${selectedPerspective}:`,
            err
          );

          setVideoSources((prev) => ({
            ...prev,
            [key]: null,
          }));
        });
    });
  }, [items, selectedPerspective, videoSources]);

  if (!items || items.length === 0) {
    return null;
  }

  const handleDragEnd = (result) => {
    if (!dragEnabled) return;
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const newOrder = Array.from(items);
    const [removed] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, removed);

    if (onReorder) {
      onReorder(newOrder);
    }
  };

  const handleLoadedMetadata = (event) => {
    const video = event.currentTarget;

    try {
      if (video.duration && video.duration > 0) {
        video.currentTime = 0.01;
      }
    } catch (err) {
      console.warn('Unable to seek timeline preview video:', err);
    }
  };

  const renderTimelineBlock = (item, index) => {
    const isSelected = Number(item.sequence_item_id) === Number(selectedItemId);
    const videoKey = `${item.content_id}-${selectedPerspective}`;
    const previewVideo = videoSources[videoKey];

    return (
      <div
        ref={isSelected ? selectedItemRef : null}
        className={`${styles.timelineBlock} ${dragEnabled ? styles.dragEnabled : ''} ${
          isSelected ? styles.selectedBlock : ''
        }`}
        role="button"
        tabIndex={0}
        title={item.name}
        onClick={() => {
          if (onSelectItem) onSelectItem(item);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onSelectItem) {
            onSelectItem(item);
          }
        }}
      >
        <div className={styles.timelineTopRow}>
          <span className={styles.timelineIndex}>{index + 1}</span>
          <span className={styles.timelineName}>{item.name}</span>
        </div>

        <div
          className={styles.timelinePreviewFrame}
          onMouseEnter={() => {
            if (previewVideo) {
              setHoveredPreview({
                video: previewVideo,
                name: item.name,
                index,
              });
            }
          }}
          onMouseLeave={() => {
            setHoveredPreview(null);
          }}
        >
          {previewVideo ? (
            <video
              className={styles.timelinePreviewVideo}
              src={previewVideo}
              preload="metadata"
              muted
              playsInline
              onLoadedMetadata={handleLoadedMetadata}
            />
          ) : (
            <span className={styles.timelinePreviewText}>
              {selectedPerspective.replace('_', ' ')}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.timelineWrapper}>
      <div className={styles.timelineHeader}>Sequence Timeline</div>

      {dragEnabled ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="sequence-timeline" direction="horizontal">
            {(provided) => (
              <div
                className={styles.timelineScroll}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {items.map((item, index) => (
                  <Draggable
                    key={item.sequence_item_id}
                    draggableId={`timeline-item-${item.sequence_item_id}`}
                    index={index}
                  >
                    {(dragProvided, snapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        className={`${styles.draggableItem} ${
                          snapshot.isDragging ? styles.dragging : ''
                        }`}
                        style={dragProvided.draggableProps.style}
                      >
                        {renderTimelineBlock(item, index)}
                      </div>
                    )}
                  </Draggable>
                ))}

                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <div className={styles.timelineScroll}>
          {items.map((item, index) => (
            <div className={styles.draggableItem} key={item.sequence_item_id}>
              {renderTimelineBlock(item, index)}
            </div>
          ))}
        </div>
      )}

      {hoveredPreview && (
        <div className={styles.hoverPlayer}>
          <div className={styles.hoverPlayerHeader}>
            <span>{hoveredPreview.index + 1}</span>
            <span>{hoveredPreview.name}</span>
          </div>

          <video
            className={styles.hoverPlayerVideo}
            src={hoveredPreview.video}
            autoPlay
            loop
            muted
            playsInline
          />
        </div>
      )}
    </div>
  );
};

export default SequenceTimeline;